import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { WeddingCardData } from '@/types'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { Marigold } from '@/components/generals/ornament'
import { WeddingCard } from '@/components/generals/wedding-card'
import { ConsultationBand } from '@/components/generals/consultation-band'
import { getWeddingCards } from '@/lib/weddings'

interface WeddingsPageProps {
    weddings: Array<WeddingCardData>
}

const WeddingsPage: FC<WeddingsPageProps> = ({ weddings }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Real weddings"
                description="Real Indian weddings we have planned: palace pheras in Udaipur, beach sangeets in Goa, a backwater sadya in Kerala and more."
            />

            <PageHero
                eyebrow="Real weddings"
                title={<>Celebrations we have had <em className='text-primary'>the joy of planning</em></>}
                description="Palace pheras, beach sangeets, temple-town mornings and garden nikahs, each one planned around a different family."
            />

            <section id='all-weddings' aria-label="All real weddings" className='py-20 lg:py-28'>
                <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                    {weddings.length > 0 ? (
                        <div className='grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3'>
                            {weddings.map(wedding => (
                                <WeddingCard key={wedding.id} wedding={wedding} headingLevel='h2' />
                            ))}
                        </div>
                    ) : (
                        <div className='mx-auto max-w-xl rounded-3xl border border-dashed border-border bg-card px-8 py-16 text-center'>
                            <Marigold className='mx-auto size-10 text-marigold' />

                            <h2 className='mt-6 text-3xl text-foreground'>
                                Our real weddings are on their way
                            </h2>

                            <p className='mt-3 leading-relaxed text-muted-foreground'>
                                We are adding stories from recent seasons. In the meantime, we would love to hear about yours.
                            </p>

                            <ButtonLink href='/contact' className='mt-8'>
                                Tell us about your wedding
                            </ButtonLink>
                        </div>
                    )}
                </div>
            </section>

            <ConsultationBand />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WeddingsPageProps> = async () => {
    try {
        const weddings = await getWeddingCards()

        return { props: { weddings }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load the real weddings:', error instanceof Error ? error.message : error)

        return { props: { weddings: [] }, revalidate: 30 }
    }
}

export default WeddingsPage
